import React from 'react'
import { Sidebar, Button, Text, Input, LoadingSpinner, Heading } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { crmService } from '../../services/crm'
import { CRMCompany } from '../../types/crm'

const CRMEmpresas: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const [companies, setCompanies] = React.useState<CRMCompany[]>([])
  const [query, setQuery] = React.useState<string>('')

  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Partial<CRMCompany> | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    const data = await crmService.listCompanies()
    setCompanies(data)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  const filtered = React.useMemo(() => {
    if (!query) return companies
    const q = query.toLowerCase()
    return companies.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.cnpj || '').toLowerCase().includes(q)
    )
  }, [companies, query])

  const openNew = () => {
    setEditing({ name: '', email: '', phone: '', cnpj: '', website: '', address: '' })
    setIsModalOpen(true)
  }

  const openEdit = (c: CRMCompany) => {
    setEditing({ ...c })
    setIsModalOpen(true)
  }

  const save = async () => {
    if (!editing?.name) return
    await crmService.upsertCompany(editing as any)
    setIsModalOpen(false)
    setEditing(null)
    await load()
  }

  const remove = async (id: string) => {
    if (!window.confirm('Excluir esta empresa?')) return
    await crmService.deleteCompany(id)
    await load()
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName="crm_empresas" permissionType="show">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">Empresas</Heading>
                <Text size="sm" color="muted" className="mt-2">Gerencie empresas do CRM</Text>
              </div>
              <PermissionGuard moduleName="crm_empresas" permissionType="insert" fallback={null}>
                <Button variant="solid" onClick={openNew}>Nova empresa</Button>
              </PermissionGuard>
            </div>
            {/* Filtros e busca */}
            <div className="mb-4 flex items-center gap-3">
              <div className="w-full sm:w-80">
                <Input
                  placeholder="Buscar por nome, e-mail, telefone ou CNPJ..."
                  value={query}
                  onChange={(e: any) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Tabela */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/30">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CNPJ</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        Nenhuma empresa encontrada.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{c.name}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{c.cnpj || '—'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{c.email || '—'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{c.phone || '—'}</td>
                        <td className="px-4 py-2 text-right space-x-2">
                          <PermissionGuard moduleName="crm_empresas" permissionType="update" fallback={null}>
                            <Button variant="outline" size="sm" onClick={() => openEdit(c)}>Editar</Button>
                          </PermissionGuard>
                          <PermissionGuard moduleName="crm_empresas" permissionType="delete" fallback={null}>
                            <Button variant="ghost" size="sm" onClick={() => remove(c.id)}>Excluir</Button>
                          </PermissionGuard>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal simples */}
            {isModalOpen && editing && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Text size="lg" className="text-gray-900 dark:text-white">
                      {editing?.id ? 'Editar empresa' : 'Nova empresa'}
                    </Text>
                    <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>Fechar</Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome</Text>
                      <Input
                        value={editing.name || ''}
                        onChange={(e: any) => setEditing((prev) => ({ ...prev!, name: e.target.value }))}
                        placeholder="Nome da empresa"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">CNPJ</Text>
                        <Input
                          value={editing.cnpj || ''}
                          onChange={(e: any) => setEditing((prev) => ({ ...prev!, cnpj: e.target.value }))}
                          placeholder="00.000.000/0000-00"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Telefone</Text>
                        <Input
                          value={editing.phone || ''}
                          onChange={(e: any) => setEditing((prev) => ({ ...prev!, phone: e.target.value }))}
                          placeholder="(00) 00000-0000"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">E-mail</Text>
                        <Input
                          value={editing.email || ''}
                          onChange={(e: any) => setEditing((prev) => ({ ...prev!, email: e.target.value }))}
                          placeholder="contato@empresa.com"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Website</Text>
                        <Input
                          value={editing.website || ''}
                          onChange={(e: any) => setEditing((prev) => ({ ...prev!, website: e.target.value }))}
                          placeholder="https://empresa.com"
                        />
                      </div>
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Endereço</Text>
                      <Input
                        value={editing.address || ''}
                        onChange={(e: any) => setEditing((prev) => ({ ...prev!, address: e.target.value }))}
                        placeholder="Rua, número, bairro, cidade"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>Cancelar</Button>
                    <Button variant="solid" onClick={save}>Salvar</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default CRMEmpresas

